import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { Toaster } from './components/ui/sonner';
import { AnimatePresence, motion } from 'framer-motion';

// Components
import { InstallPrompt } from './components/PWA';
import { OnboardingFlow } from './components/Onboarding';
import SplashScreen from './components/SplashScreen';

// ScrollToTop component - scrolls to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Public Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import HowItWorksPublic from './pages/public/HowItWorks';
import About from './pages/public/About';
import Network from './pages/public/Network';
import Benefits from './pages/public/Benefits';
import Contact from './pages/public/Contact';
import GiftCards from './pages/public/GiftCards';
import MerchantPricing from './pages/public/MerchantPricing';
import PaymentCard from './pages/public/PaymentCard';
import Crypto from './pages/public/Crypto';
import Loyalty from './pages/public/Loyalty';
import GiftOffer from './pages/public/GiftOffer';
import Activate from './pages/public/Activate';

// Consumer Pages
import Wallet from './pages/consumer/Wallet';
import Scanner from './pages/consumer/Scanner';
import SendGift from './pages/consumer/SendGift';
import Rewards from './pages/consumer/Rewards';
import Profile from './pages/consumer/Profile';
import GiftSuccess from './pages/consumer/GiftSuccess';
import Explore from './pages/consumer/Explore';
import HowItWorks from './pages/consumer/HowItWorks';
import TopUp from './pages/consumer/TopUp';
import ChargeCard from './pages/consumer/ChargeCard';

// Merchant Pages
import MerchantDashboard from './pages/merchant/Dashboard';
import MerchantOnboard from './pages/merchant/Onboard';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';

// Hide splash screen when React loads
const hideSplashScreen = () => {
  const splash = document.getElementById('splash-screen');
  if (splash) {
    splash.classList.add('hide');
    setTimeout(() => splash.remove(), 500);
  }
};

// Page transition wrapper with language-aware key for re-render on language change
const PageWrapper = ({ children }) => {
  const location = useLocation();
  const { language } = useLanguage();
  
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={`${location.pathname}-${language}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

// Loading screen
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0F1A14]">
    <div className="text-center">
      <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#C3A365] border-r-transparent"></div>
      <p className="mt-4 text-[#8BA89A]">Chargement...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children, role }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user || user === false) {
    return <Navigate to="/login" replace />;
  }

  if (role && user.role !== role) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <PageWrapper>
      <ScrollToTop />
      <Routes location={location}>
        {/* Public Routes */}
        <Route path="/" element={user && user !== false ? <Navigate to={user.role === 'admin' ? '/admin' : user.role === 'merchant' ? '/merchant' : '/wallet'} replace /> : <Landing />} />
        <Route path="/login" element={user && user !== false ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/register" element={user && user !== false ? <Navigate to="/" replace /> : <Register />} />
        
        {/* Public Info Pages */}
        <Route path="/comment-ca-marche" element={<HowItWorksPublic />} />
        <Route path="/a-propos" element={<About />} />
        <Route path="/reseau" element={<Network />} />
        <Route path="/avantages" element={<Benefits />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/cartes-cadeaux" element={<GiftCards />} />
        <Route path="/tarifs-marchands" element={<MerchantPricing />} />
        <Route path="/carte-paiement" element={<PaymentCard />} />
        <Route path="/crypto" element={<Crypto />} />
        <Route path="/fidelite" element={<Loyalty />} />
        <Route path="/offrir" element={<GiftOffer />} />
        <Route path="/activer" element={<Activate />} />
        
        {/* Consumer Routes */}
        <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
        <Route path="/scanner" element={<ProtectedRoute><Scanner /></ProtectedRoute>} />
        <Route path="/explore" element={<ProtectedRoute><Explore /></ProtectedRoute>} />
        <Route path="/send-gift" element={<ProtectedRoute><SendGift /></ProtectedRoute>} />
        <Route path="/rewards" element={<ProtectedRoute><Rewards /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/gift-success" element={<ProtectedRoute><GiftSuccess /></ProtectedRoute>} />
        <Route path="/how-it-works" element={<ProtectedRoute><HowItWorks /></ProtectedRoute>} />
        <Route path="/topup" element={<ProtectedRoute><TopUp /></ProtectedRoute>} />
        <Route path="/charge-card" element={<ProtectedRoute><ChargeCard /></ProtectedRoute>} />
        
        {/* Merchant Routes */}
        <Route path="/merchant/onboard" element={<ProtectedRoute><MerchantOnboard /></ProtectedRoute>} />
        <Route path="/merchant" element={<ProtectedRoute role="merchant"><MerchantDashboard /></ProtectedRoute>} />
        
        {/* Admin Routes */}
        <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </PageWrapper>
  );
};

function App() {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    // Check if user has seen splash before in this session
    const hasSeenSplash = sessionStorage.getItem('habitue-splash-seen');
    if (hasSeenSplash) {
      setShowSplash(false);
    }
    
    // Hide loading splash screen
    const timer = setTimeout(hideSplashScreen, 800);
    return () => clearTimeout(timer);
  }, []);

  const handleSplashEnter = () => {
    sessionStorage.setItem('habitue-splash-seen', 'true');
    setShowSplash(false);
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  return (
    <ThemeProvider>
    <CurrencyProvider>
    <LanguageProvider>
    <AuthProvider>
      <div className="App">
        {/* Splash Screen */}
        {showSplash && <SplashScreen onEnter={handleSplashEnter} />}
        
        <BrowserRouter>
          {/* Onboarding Flow for new users */}
          <AnimatePresence>
            {showOnboarding && (
              <OnboardingFlow onComplete={handleOnboardingComplete} />
            )}
          </AnimatePresence>
          
          {/* Main App Routes */}
          <AppRoutes />
          
          {/* PWA Install Prompt */}
          <InstallPrompt />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </div>
    </AuthProvider>
    </LanguageProvider>
    </CurrencyProvider>
    </ThemeProvider>
  );
}

export default App;
